import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  Input,
  Card,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
  Modal,
} from "antd";
import { SendOutlined, FileTextOutlined, TeamOutlined } from "@ant-design/icons";
import Logo from "../../components/general/Logo";
import LeoVerifiedBidBadge from "../../components/vendor/LeoVerifiedBidBadge";
import PreferredEdgePromo from "../../components/vendor/PreferredEdgePromo";
import { getApiUrl } from "../../lib/api/config";
import { Helmet } from "react-helmet";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const TERMS_STORAGE_PREFIX = "lo_vendor_negotiate_terms_v1_";

function termsAcceptedForToken(bidToken) {
  if (typeof window === "undefined" || !bidToken) return false;
  try {
    return sessionStorage.getItem(`${TERMS_STORAGE_PREFIX}${bidToken}`) === "1";
  } catch {
    return false;
  }
}

/**
 * Public Leo procurement chat (/negotiate/:bidToken)
 */
const VendorNegotiationPage = () => {
  const { bidToken } = useParams();
  const [searchParams] = useSearchParams();
  const [termsAccepted, setTermsAccepted] = useState(() =>
    termsAcceptedForToken(bidToken)
  );
  const [loading, setLoading] = useState(() => termsAcceptedForToken(bidToken));
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sourcing, setSourcing] = useState(false);
  const [sourceNote, setSourceNote] = useState(null);
  const [preferredVendor, setPreferredVendor] = useState(false);
  const bottomRef = useRef(null);

  const apiBase = getApiUrl();

  useEffect(() => {
    setTermsAccepted(termsAcceptedForToken(bidToken));
  }, [bidToken]);

  useEffect(() => {
    if (!bidToken || !termsAccepted) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          `${apiBase}/public/leo-vendor-chat/session?bidToken=${encodeURIComponent(
            bidToken
          )}`
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || "Could not load negotiation session");
        }
        if (!cancelled) {
          setSession(data);
          setMessages(data.messages || []);
          setPreferredVendor(Boolean(data.isPreferredVendor));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Failed to load");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [bidToken, termsAccepted, apiBase, searchParams]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const acceptTermsAndStart = () => {
    if (!bidToken) return;
    try {
      sessionStorage.setItem(`${TERMS_STORAGE_PREFIX}${bidToken}`, "1");
    } catch {
      void 0;
    }
    setTermsAccepted(true);
    setLoading(true);
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || !bidToken) return;
    try {
      setSending(true);
      const res = await fetch(`${apiBase}/public/leo-vendor-chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bidToken, message: text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Send failed");
      }
      setMessages(data.messages || []);
      setDraft("");
    } catch (e) {
      setError(e.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const sourceVendors = async () => {
    if (!bidToken) return;
    try {
      setSourcing(true);
      setSourceNote(null);
      const res = await fetch(`${apiBase}/public/leo-vendor-chat/source-vendors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bidToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Sourcing failed");
      }
      setSourceNote(
        `Recorded ${data.count || 0} local lead(s) for LandlordOps outreach.`
      );
    } catch (e) {
      setSourceNote(e.message || "Sourcing failed");
    } finally {
      setSourcing(false);
    }
  };

  if (!termsAccepted) {
    return (
      <Page>
        <Helmet>
          <title>Vendor terms | LandlordOps</title>
        </Helmet>
        <Shell>
          <Logo size="large" displayMode="both" />
          <Modal
            open
            closable={false}
            maskClosable={false}
            footer={null}
            width={440}
            centered
            styles={{ body: { paddingTop: 8 } }}
          >
            <Title level={4} style={{ marginTop: 0 }}>
              Vendor terms
            </Title>
            <Paragraph style={{ marginBottom: 16 }}>
              By proceeding, you agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                LandlordOps Vendor Terms
              </a>
              , including a <strong>10% Platform Success Fee</strong> upon job
              completion.
            </Paragraph>
            <Button type="primary" block size="large" onClick={acceptTermsAndStart}>
              Accept &amp; Start
            </Button>
          </Modal>
        </Shell>
      </Page>
    );
  }

  if (loading && !session) {
    return (
      <Page>
        <Centered>
          <Spin size="large" />
        </Centered>
      </Page>
    );
  }

  if (error && !session) {
    return (
      <Page>
        <Centered>
          <Logo size="large" displayMode="both" />
          <Alert type="error" message={error} style={{ maxWidth: 480 }} />
        </Centered>
      </Page>
    );
  }

  const preferred = session?.isPreferredVendor ?? preferredVendor;
  const clientAlias = session?.clientAlias || "LandlordOps Management";

  return (
    <Page>
      <Helmet>
        <title>Leo AI procurement | {clientAlias}</title>
      </Helmet>
      <Shell>
        <NegotiateLayout>
          <NegotiateMain>
        <HeaderBar>
          <Logo size="medium" displayMode="both" />
          <Space align="center" wrap>
            <Title level={4} style={{ margin: 0 }}>
              Procurement: {clientAlias}
            </Title>
            {preferred ? <LeoVerifiedBidBadge /> : null}
          </Space>
          <MetaRow>
            {session?.propertyZip && (
              <Tag color="default">ZIP {session.propertyZip}</Tag>
            )}
            <Tag color="blue">{session?.tradeCategory || "Maintenance"}</Tag>
            {preferred && session?.lowestPendingBid != null && (
              <Tag color="green">
                Low bid on file: $
                {Number(session.lowestPendingBid).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Tag>
            )}
          </MetaRow>
        </HeaderBar>

        {error && (
          <Alert
            type="warning"
            showIcon
            message={error}
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <IssueCard title="Work scope summary" size="small">
          <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
            {session?.issueSummary || "-"}
          </Paragraph>
        </IssueCard>

        <ChatCard
          title="Conversation"
          extra={
            <Button
              type="link"
              icon={<FileTextOutlined />}
              onClick={() => {
                if (session?.bidSubmitPath) {
                  window.open(
                    session.bidSubmitPath,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }
              }}
            >
              Submit formal bid
            </Button>
          }
        >
          <MessageList>
            {(messages || []).map((m, i) => (
              <Bubble key={i} $leo={m.role === "leo"}>
                <BubbleLabel>{m.role === "leo" ? "Leo AI" : "You"}</BubbleLabel>
                <BubbleText>{m.content}</BubbleText>
              </Bubble>
            ))}
            {sending && (
              <TypingBubble $leo>
                <BubbleLabel>Leo AI</BubbleLabel>
                <BubbleText>
                  <em>Leo AI is analyzing…</em>
                </BubbleText>
              </TypingBubble>
            )}
            <div ref={bottomRef} />
          </MessageList>
          <Composer>
            <TextArea
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Confirm ZIP coverage, ETA, and proposed bid…"
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              onClick={send}
            >
              Send
            </Button>
          </Composer>
        </ChatCard>

        <ToolsRow>
          <Button
            icon={<TeamOutlined />}
            loading={sourcing}
            onClick={sourceVendors}
          >
            Request additional local contractor leads (Leo AI sourced)
          </Button>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Uses public business listings near the job ZIP. Emails are captured
            when published on the contractor website.
          </Text>
        </ToolsRow>
        {sourceNote && (
          <Alert type="info" message={sourceNote} style={{ marginTop: 12 }} />
        )}

        <FooterNav>
          <Link to={`/bid/${bidToken}`}>Go to bid form</Link>
        </FooterNav>
          </NegotiateMain>
          <NegotiateAside>
            <PreferredEdgePromo
              bidToken={bidToken}
              returnPage="negotiate"
              isPreferred={preferred}
              onPreferredChange={(v) => {
                setPreferredVendor(v);
                setSession((prev) =>
                  prev ? { ...prev, isPreferredVendor: v } : prev
                );
              }}
            />
          </NegotiateAside>
        </NegotiateLayout>
      </Shell>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 40%, #334155 100%);
  padding: 1.5rem 1rem 3rem;
`;

const Shell = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const NegotiateLayout = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const NegotiateMain = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 720px;
`;

const NegotiateAside = styled.aside`
  width: 100%;
  max-width: 320px;
  flex-shrink: 0;
  position: sticky;
  top: 1rem;
  @media (max-width: 900px) {
    position: static;
    max-width: none;
  }
`;

const HeaderBar = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`;

const MetaRow = styled.div`
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const IssueCard = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 10px;
`;

const ChatCard = styled(Card)`
  border-radius: 10px;
  margin-bottom: 1rem;
`;

const MessageList = styled.div`
  max-height: 420px;
  overflow-y: auto;
  padding: 8px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Bubble = styled.div`
  align-self: ${(p) => (p.$leo ? "flex-start" : "flex-end")};
  max-width: 92%;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${(p) => (p.$leo ? "#e0f2fe" : "#1d4ed8")};
  color: ${(p) => (p.$leo ? "#0c4a6e" : "#fff")};
`;

const TypingBubble = styled(Bubble)`
  opacity: 0.92;
  animation: pulse 1.2s ease-in-out infinite;
  @keyframes pulse {
    50% {
      opacity: 0.75;
    }
  }
`;

const BubbleLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
  margin-bottom: 4px;
`;

const BubbleText = styled.div`
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Composer = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const ToolsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 4px;
`;

const FooterNav = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  a {
    color: #93c5fd;
    font-weight: 500;
  }
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  min-height: 50vh;
`;

export default VendorNegotiationPage;
